import { useEffect } from 'react'

type SeoProps = {
  title: string
  description: string
  image?: string
  path?: string
}

const DEFAULT_IMAGE = '/carro_defeito.webp'

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)

  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }

  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value)
  }
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLLinkElement>(selector)

  if (!element) {
    element = document.createElement('link')
    document.head.appendChild(element)
  }

  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value)
  }
}

export function Seo({ title, description, image = DEFAULT_IMAGE, path = '/' }: SeoProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL?.trim() || window.location.origin
    const canonicalUrl = new URL(path, siteUrl.endsWith('/') ? siteUrl : `${siteUrl}/`).toString()
    const resolvedImage = new URL(image, canonicalUrl).toString()

    document.documentElement.lang = 'pt-BR'
    document.title = title

    upsertMeta('meta[name="description"]', { name: 'description', content: description })
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
    })
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title })
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description })
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'pt_BR' })
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'WeAuto' })
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: resolvedImage })
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description })
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: resolvedImage })
    upsertMeta('meta[name="twitter:image:alt"]', {
      name: 'twitter:image:alt',
      content: 'WeAuto - avaliação veicular com fotos e fechamento',
    })
    upsertMeta('meta[name="theme-color"]', { name: 'theme-color', content: '#0f4c81' })
    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl })

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'WeAuto',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description,
      url: canonicalUrl,
      image: resolvedImage,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'BRL',
      },
      provider: {
        '@type': 'Organization',
        name: 'WeAuto',
        url: siteUrl,
      },
    }

    let script = document.head.querySelector<HTMLScriptElement>('script[data-seo-jsonld="weauto-home"]')

    if (!script) {
      script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-seo-jsonld', 'weauto-home')
      document.head.appendChild(script)
    }

    script.textContent = JSON.stringify(schema)

    return () => {
      script?.remove()
    }
  }, [title, description, image, path])

  return null
}
